import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import SelectDialog from '../components/SelectDialog';
import { stampLoginVersion } from '@/lib/loginVersion';
import { base44 } from '@/api/base44Client';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import PhoneCodeVerifier from '@/components/auth/PhoneCodeVerifier';
import PasswordStrength from '@/components/auth/PasswordStrength';
import PasswordMatch from '@/components/auth/PasswordMatch';
import MorphButton from '@/components/auth/MorphButton';
import AuthTabs from '@/components/auth/AuthTabs';
import LoyaltyJoinPrompt from '@/components/onboarding/LoyaltyJoinPrompt';

/**
 * Registration flow:
 * Step 1: Email + password → base44.auth.register()
 * Step 2: Email OTP verification → setToken → now authenticated
 * Step 3: Enter phone number → lookup Square
 * Step 4: Verify phone (Twilio SMS)
 * Step 5: Review / fill in profile (auto-filled from Square if found) → syncUserSquareProfile → Home
 */

export default function Register() {
  const [step, setStep] = useState(1);

  // Show the shell logo only on the sign-up card (step 1); hide it on every later step.
  useEffect(() => {
    document.body.classList.toggle('auth-hide-logo', step !== 1);
    return () => document.body.classList.remove('auth-hide-logo');
  }, [step]);
  const [isLoading, setIsLoading] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const rawNext = new URLSearchParams(location.search).get('next');
  const nextUrl = rawNext && /^\/(?![/\\])/.test(rawNext) ? rawNext : '/';

  // Step 1 & 2 state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');

  // Step 3 & 4 state
  const [countryCode, setCountryCode] = useState('+44');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [twilioCode, setTwilioCode] = useState('');
  const [squareCustomer, setSquareCustomer] = useState(null); // null = not found, object = found

  // Step 5 state (profile)
  const [profile, setProfile] = useState({
    firstName: '', lastName: '', birthday: '',
    addressLine1: '', addressLine2: '', city: '', postcode: ''
  });

  const fullPhone = `${countryCode}${phoneNumber}`;

  // Rolling birthday limits: must be at least 16 years old, and no older than 100.
  const shiftYears = (years) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - years);
    return d.toISOString().slice(0, 10);
  };
  const maxBirthday = shiftYears(16);
  const minBirthday = shiftYears(100);

  // ── Step 1 → 2: Register Base44 account ──
  // We never ask the server "does this email exist?" before sign-up — that would
  // be an account-enumeration oracle. The duplicate is reported by register()
  // itself, to someone who submitted their own email and password.
  const handleRegister = async () => {
    if (!email.trim() || !password) { toast.error('Please enter your email and password'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setIsLoading(true);
    try {
      const check = await base44.functions.invoke('checkEmailDomain', { email });
      if (check.data?.blocked) { toast.error('Registrations from this email domain are not accepted. Please use a different email.'); return; }
      await base44.auth.register({ email, password });
      toast.success('Check your email for a verification code!');
      setStep(2);
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      if (msg.includes('exists') || msg.includes('already') || msg.includes('registered')) {
        setEmailTaken(true);
      } else {
        toast.error(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2: Verify email OTP → now logged in ──
  const checkEmailCode = (code) => base44.auth.verifyOtp({ email, otpCode: code }).catch(() => null);

  const handleVerifyOtp = async (res) => {
    setIsLoading(true);
    try {
      base44.auth.setToken(res.access_token);
      await stampLoginVersion().catch(() => {});
      toast.success('Email verified!');

      // Notify admin of new registration (fire and forget)
      base44.functions.invoke('notifyNewUserRegistration', { data: { email } }).catch(() => {});
      // Send welcome email to the new user (fire and forget)
      base44.functions.invoke('sendWelcomeEmail', { data: { email, full_name: '' } }).catch(() => {});

      setStep(3);
    } catch (err) {
      toast.error(err.message || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 3: Check phone uniqueness then send Twilio SMS ──
  const handleSendCode = async () => {
    if (!phoneNumber.trim()) { toast.error('Please enter your phone number'); return; }
    setIsLoading(true);
    try {
      // FIRST check if this phone is already linked to a registered user
      const phoneCheck = await base44.functions.invoke('checkPhoneExists', { phone_number: fullPhone });
      if (phoneCheck.data?.exists) {
        toast.error('This phone number is already linked to an existing account. Please use a different number.');
        setIsLoading(false);
        return;
      }

      // Only send Twilio if the phone is not already registered
      await base44.functions.invoke('initiateTwilioVerification', { phone_number: fullPhone });
      toast.success('Verification code sent!');
      setCodeSent(true);
      await new Promise((r) => setTimeout(r, 900));
      setCodeSent(false);
      setStep(4);
    } catch {
      toast.error('Failed to send code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 4: Verify Twilio SMS → lookup Square ──
  const verifyPhoneCode = async (code) => {
    const res = await base44.functions.invoke('checkTwilioVerification', { phone_number: fullPhone, code });
    return res.data?.verified ? res : null;
  };

  const handleVerifyPhone = async (res) => {
    setIsLoading(true);
    try {

      // Now lookup Square to pre-fill details — post-verification only
      const lookupRes = await base44.functions.invoke('lookupSquareCustomer', {
        phone_number: fullPhone,
        session_token: res.data.session_token,
        square_customer_id: res.data.square_customer_id
      });
      const found = lookupRes.data?.found && lookupRes.data?.customer;
      if (found) {
        const c = lookupRes.data.customer;
        setSquareCustomer(c);
        setProfile({
          firstName: c.given_name || '', lastName: c.family_name || '',
          birthday: '', addressLine1: '', addressLine2: '', city: '', postcode: ''
        });
        toast.success('We found your existing account — details pre-filled!');
      } else {
        setSquareCustomer(null);
        setProfile({ firstName: '', lastName: '', birthday: '', addressLine1: '', addressLine2: '', city: '', postcode: '' });
      }
      setStep(5);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 5: Save profile & sync Square ──
  const handleSaveProfile = async () => {
    if (!profile.firstName.trim() || !profile.lastName.trim()) { toast.error('Please enter your first and last name'); return; }
    if (profile.birthday && (profile.birthday > maxBirthday || profile.birthday < minBirthday)) {
      toast.error('Please enter a valid date of birth — you must be at least 16 years old.');
      return;
    }
    setIsLoading(true);
    try {
      const firstName = profile.firstName.trim();
      const lastName = profile.lastName.trim();
      const syncRes = await base44.functions.invoke('syncUserSquareProfile', {
        phone_number: fullPhone,
        full_name: `${firstName} ${lastName}`,
        first_name: firstName,
        last_name: lastName,
        email,
        birthday: profile.birthday || undefined,
        address_line_1: profile.addressLine1 || undefined,
        address_line_2: profile.addressLine2 || undefined,
        city: profile.city || undefined,
        postcode: profile.postcode || undefined
      });
      toast.success('Account set up successfully!');

      // ── Set customer_data NOW from the function response so getCartSessionId()
      //    returns basket_<square_id> on next page load — prevents guest cart wipe
      const squareCustomerId = syncRes.data?.square_customer_id;
      if (squareCustomerId) {
        const oldGuest = localStorage.getItem('cart_session_id');
        if (oldGuest && !oldGuest.startsWith('basket_')) {
          localStorage.setItem('pending_cart_migration', oldGuest);
        }
        localStorage.setItem('customer_data', JSON.stringify({
          square_customer_id: squareCustomerId,
          loyalty_account_id: syncRes.data?.loyalty_account_id || null
        }));
      }

      // Clear any pending onboarding data
      sessionStorage.removeItem('pending_onboarding');

      // Existing rewards members go straight to their Loyalty page; new members
      // get the join breakdown first, then the Loyalty page to press Join.
      if (syncRes.data?.loyalty_found) {
        window.location.href = '/Loyalty';
        return;
      }
      setStep(6);
    } catch {
      toast.error('Could not save your profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const countryCodes = [
    { value: '+44', label: '🇬🇧 +44' },

  ];

  return (
      <div className="flex-1 flex flex-col">
        {step === 1 && !emailTaken && <AuthTabs active="register" />}


        {/* ── STEP 1: Email Already Registered — stop duplicate sign-up ── */}
        {step === 1 && emailTaken && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-2" style={{ fontFamily: 'Georgia, serif' }}>Account already exists</h1>
            <p className="text-gray-500 text-center mb-6 text-sm">
              A user with the email <span className="font-semibold text-gray-800">{email}</span> is already registered. Please sign in with your password, or reset it if you've forgotten it.
            </p>

            <button
              onClick={() => navigate(`/login${nextUrl !== '/' ? `?next=${encodeURIComponent(nextUrl)}` : ''}`)}
              className="w-full text-white rounded-full py-3 text-base font-semibold transition-opacity hover:opacity-90 mb-3"
              style={{ backgroundColor: '#1A2744' }}
            >
              Sign in with your password
            </button>
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full border-2 rounded-full py-3 text-base font-semibold transition-opacity hover:opacity-90 mb-3"
              style={{ borderColor: '#1A2744', color: '#1A2744', backgroundColor: 'white' }}
            >
              Forgot your password?
            </button>

            <p className="text-sm text-gray-500 text-center mt-4">
              Wrong email?{' '}
              <button
                onClick={() => { setEmailTaken(false); setEmail(''); setPassword(''); setConfirmPassword(''); }}
                className="font-semibold hover:underline"
                style={{ color: '#1A2744' }}
              >
                Use a different email
              </button>
            </p>
          </>
        )}

        {/* ── STEP 1: Email + Password ── */}
        {step === 1 && !emailTaken && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-2" style={{ fontFamily: 'Georgia, serif' }}>Create your account</h1>
            <p className="text-gray-500 text-center mb-6 text-sm">Join Peony Rewards and start earning points.</p>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email Address *</label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Password *</label>
                <PasswordInput
                  placeholder="Min 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Confirm Password *</label>
                <PasswordInput
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleRegister()}
                  className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                />
                <PasswordMatch password={password} confirm={confirmPassword} />
                <PasswordStrength password={password} />
              </div>
            </div>

            <MorphButton onClick={handleRegister} loading={isLoading}>Continue</MorphButton>

            <div className="text-center text-sm text-gray-500 mt-auto pt-6 space-y-3">
              <p className="text-xs text-gray-400 leading-relaxed">
                Create an account to earn loyalty points, track orders and access exclusive rewards.
              </p>
              <button onClick={() => navigate('/HowToRegister')} className="text-sm hover:underline block mx-auto min-h-0 min-w-0" style={{ color: '#1A2744' }}>
                Need help? How to register
              </button>
              <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2 inline-block min-h-0 min-w-0">
                Return to Home
              </button>
            </div>
          </>
        )}

        {/* ── STEP 2: Email OTP ── */}
        {step === 2 && (
          <>
            <PhoneCodeVerifier
              phone={email}
              channel="email"
              verify={(code) => checkEmailCode(code).then((r) => r?.access_token ? r : null)}
              onVerified={handleVerifyOtp}
              onResend={() => base44.auth.resendOtp(email)}
              onBack={() => setStep(1)}
            />
          </>
        )}

        {/* ── STEP 3: Phone Number ── */}
        {step === 3 && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-2" style={{ fontFamily: 'Georgia, serif' }}>Add your phone number</h1>
            <p className="text-gray-500 text-center mb-6 text-sm">We'll check if you already have a Peony Rewards account and link it.</p>

            <label className="block text-xs font-medium text-gray-700 mb-2">Mobile Number *</label>
            <div className="flex items-center gap-2 border-2 rounded-2xl p-1 bg-white mb-1" style={{ borderColor: '#1A2744' }}>
              <span className="pl-4 text-base text-gray-900 font-medium shrink-0">🇬🇧 +44</span>
              <Input
                type="tel"
                placeholder="Mobile number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                onKeyPress={(e) => e.key === 'Enter' && handleSendCode()}
                className="border-0 bg-transparent text-base placeholder:text-gray-400 focus-visible:ring-0 px-4"
              />
            </div>
            <p className="text-xs text-gray-500 mb-6">Enter 10 digits without the leading 0</p>

            <MorphButton onClick={handleSendCode} loading={isLoading} success={codeSent}>
              Send Verification Code
            </MorphButton>
          </>
        )}

        {/* ── STEP 4: Verify Phone (Twilio) ── */}
        {step === 4 && (
          <>
            <PhoneCodeVerifier
              phone={fullPhone}
              verify={verifyPhoneCode}
              onVerified={handleVerifyPhone}
              onResend={() => base44.functions.invoke('initiateTwilioVerification', { phone_number: fullPhone })}
              onBack={() => { setStep(3); setTwilioCode(''); }}
            />
          </>
        )}

        {/* ── STEP 5: Profile Details ── */}
        {step === 5 && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-1" style={{ fontFamily: 'Georgia, serif' }}>Your details</h1>

            {squareCustomer ? (
              <div className="flex items-center gap-2 justify-center mb-5 text-sm text-green-700 bg-green-50 rounded-xl px-3 py-2 border border-green-200">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span>Existing Peony account found — details pre-filled!</span>
              </div>
            ) : (
              <p className="text-gray-500 text-center mb-5 text-sm">Tell us a little about yourself.</p>
            )}

            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                  <Input
                    placeholder="John"
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                    className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Last Name *</label>
                  <Input
                    placeholder="Doe"
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                    className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date of Birth (Optional)</label>
                <Input
                  type="date"
                  value={profile.birthday}
                  min={minBirthday}
                  max={maxBirthday}
                  onChange={(e) => setProfile({ ...profile, birthday: e.target.value })}
                  className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                />
                <p className="text-xs text-gray-500 mt-1">You must be at least 16 years old.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Address Line 1 (Optional)</label>
                <Input
                  placeholder="Street address"
                  value={profile.addressLine1}
                  onChange={(e) => setProfile({ ...profile, addressLine1: e.target.value })}
                  className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                  <Input
                    placeholder="City"
                    value={profile.city}
                    onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                    className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                  <Input
                    placeholder="DE75 7NE"
                    value={profile.postcode}
                    onChange={(e) => setProfile({ ...profile, postcode: e.target.value })}
                    className="border-2 rounded-xl h-12" style={{ borderColor: '#1A2744' }}
                  />
                </div>
              </div>
            </div>

            <MorphButton onClick={handleSaveProfile} loading={isLoading}>Complete Setup</MorphButton>
          </>
        )}

        {/* ── STEP 6: Join Peony Rewards offer ── */}
        {step === 6 && (
          <LoyaltyJoinPrompt
            onJoin={async () => {
              setIsLoading(true);
              try {
                const res = await base44.functions.invoke('joinLoyaltyProgram', {});
                if (res.data?.success) {
                  window.location.href = '/Loyalty?joined=1';
                  return;
                }
                toast.error(res.data?.error || 'Something went wrong. Please try again.');
              } catch (e) {
                toast.error('Something went wrong. Please try again.');
              }
              setIsLoading(false);
            }}
            joining={isLoading}
            onSkip={() => { window.location.href = nextUrl.startsWith('/') ? nextUrl : '/'; }}
          />
        )}
      </div>
  );
}